var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import { useTheme, useBreakpoints } from '../../theme';
import { useMemoClasses } from '../../utils/hooks';
import { getFlexBasis } from '../../utils/functions';
var useStyles = createUseStyles({
    base: {
        boxSizing: 'border-box',
        flexGrow: '1',
    },
    baseOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Flex.base));
    },
    container: {
        display: 'flex',
        width: '100%',
    },
    containerOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Flex.container));
    },
    xs: {
        width: '100%',
        maxWidth: function (_a) {
            var xs = _a.xs;
            return getFlexBasis(xs);
        },
    },
    sm: {
        width: '100%',
        maxWidth: function (_a) {
            var sm = _a.sm;
            return getFlexBasis(sm);
        },
    },
    md: {
        width: '100%',
        maxWidth: function (_a) {
            var md = _a.md;
            return getFlexBasis(md);
        },
    },
    lg: {
        width: '100%',
        maxWidth: function (_a) {
            var lg = _a.lg;
            return getFlexBasis(lg);
        },
    },
    xl: {
        width: '100%',
        maxWidth: function (_a) {
            var xl = _a.xl;
            return getFlexBasis(xl);
        },
    },
}, { name: 'RUIFlex' });
var Flex = function (_a) {
    var container = _a.container, direction = _a.direction, wrap = _a.wrap, justifyContent = _a.justifyContent, alignItems = _a.alignItems, alignContent = _a.alignContent, order = _a.order, className = _a.className, style = _a.style, children = _a.children, xs = _a.xs, sm = _a.sm, md = _a.md, lg = _a.lg, xl = _a.xl, props = __rest(_a, ["container", "direction", "wrap", "justifyContent", "alignItems", "alignContent", "order", "className", "style", "children", "xs", "sm", "md", "lg", "xl"]);
    var theme = useTheme();
    var styles = useStyles({ theme: theme, xs: xs, sm: sm, md: md, lg: lg, xl: xl });
    var activeBreakpoints = useBreakpoints();
    var classes = useMemoClasses([
        styles.base,
        theme.overrides.Flex.base && styles.baseOverrides,
        container && styles.container,
        container && theme.overrides.Flex.container && styles.containerOverrides,
        xs && activeBreakpoints.includes('xs') && styles.xs,
        sm && activeBreakpoints.includes('sm') && styles.sm,
        md && activeBreakpoints.includes('md') && styles.md,
        lg && activeBreakpoints.includes('lg') && styles.lg,
        xl && activeBreakpoints.includes('xl') && styles.xl,
        'RUIFlex-base',
        container && 'RUIFlex-container',
        wrap && 'RUIFlex-wrap',
        className,
    ], [theme, container, xs, sm, md, lg, xl, className, activeBreakpoints]);
    var inlineStyles = useMemo(function () {
        var mergedStyles = {};
        if (order)
            mergedStyles.order = order;
        if (container)
            mergedStyles = __assign(__assign({}, mergedStyles), { flexDirection: direction, flexWrap: wrap ? 'wrap' : 'nowrap', justifyContent: justifyContent, alignContent: alignContent, alignItems: alignItems });
        mergedStyles = __assign(__assign({}, mergedStyles), style);
        return mergedStyles;
    }, [container, direction, wrap, justifyContent, alignContent, alignItems, order, styles]);
    return (React.createElement("div", __assign({ style: inlineStyles, className: classes }, props), children));
};
Flex.defaultProps = {
    container: false,
    direction: 'row',
    wrap: true,
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    alignContent: 'stretch',
    order: 0,
    className: '',
    style: {},
};
export { Flex as default, Flex };
